<template>
  <span>
    <FormLayout
      data-test="job-side-sheet"
      :title="titleLabel"
      :is-loading="loading"
      :has-changes="draftChangesDetected"
      :is-saving="isJobSaving || isCreatingJobs"
      :save-label="saveButtonLabel"
      @on-save="saveTicket"
      @on-dismiss="dismissClicked"
      @[canDiscard]="handleDiscard"
    >
      <template #label>
        <div class="ticket-label">
          <vs-button
            v-if="!isNewTicket && hasBeenScheduled"
            icon="arrow_back"
            type="tertiary"
            data-test="back-to-readonly-job-form"
            class="back-button"
            @click="handleBackToReadonly"
          />
          <job-status-chip v-if="status" data-test="job-status" :status="finalStatus" />
        </div>
      </template>

      <template v-if="!isFetching && jobId" #breadcrumbs>
        <project-breadcrumbs
          v-if="projectId"
          data-test="job-title"
          :project-id="projectId"
          :job-display-name="displayedJobId"
          :route="projectSideSheetRoute"
        />
        <vs-text v-else class="displayed-job-id">{{ displayedJobId }}</vs-text>
      </template>

      <div v-if="!isFetching && !loadJobError && jobId" class="description">
        <created-info
          data-test="job-creation-description"
          :creator-sid="isNewTicket ? $auth.user.sub : createdBy"
          :created-time="created"
        />
      </div>

      <JobErrorBanner />

      <JobReadonlyBanner
        v-if="!loadJobError && isReadonly"
        :is-archived="isArchived"
        :job-status="status"
        :hub-id="jobHubId"
      />

      <TicketForm
        v-if="!loadJobError"
        ref="ticket-form"
        :job-id.sync="jobId"
        :job-uuid="jobUuid"
        :job-request-id="jobRequestId"
        :unit-working-status="unitWorkingStatus"
        :active-duration="activeDuration"
        :priority-id.sync="priorityId"
        :owner-id.sync="ownerId"
        :account-id="accountId"
        :customer-sid.sync="customerSid"
        :follower-ids="followerIds"
        :follower-sids-by-id="loadedFollowerSidsById"
        :start-time.sync="startTime"
        :end-time.sync="endTime"
        :activity-id.sync="activityId"
        :resource-type-id.sync="resourceTypeId"
        :crew.sync="crew"
        :unit-id.sync="unitId"
        :service-provider-business-unit-id.sync="serviceProviderBusinessUnitId"
        :cost-center.sync="costCenter"
        :details.sync="details"
        :attachments-storage-id="attachmentsStorageId"
        :custom-form-data.sync="customData"
        :time-slot-dates.sync="timeSlotDates"
        :time-slot-start.sync="timeSlotStart"
        :time-slot-duration.sync="timeSlotDuration"
        :bulk-create.sync="bulkCreate"
        :form-submissions="formSubmissions"
        :is-fetching="loading"
        :approval-status="approvalStatus"
        :attachments.sync="attachments"
        :is-readonly="isReadonly"
        :is-cancelled="isCancelled"
        :is-new="isNewTicket"
        :job-status="status"
        :assignment-group-number="assignmentGroupNumber"
        :hub-id="jobHubId"
        :pool-id="poolId"
        :assignment-form-requirements="assignmentFormRequirements"
        :batch-id="invoiceId"
        :signature="signature"
        :error-fetching-extension-schema="extensionSchemaFetchError"
        :extension-schema-id.sync="extensionSchemaId"
        :extension-schema="extensionSchema"
        :has-newer-custom-fields-schema="customFieldsNewerAvailable"
        :show-repeated-job-warning-banner="showRepeatedJobWarningBanner"
        @update:followerIds="setFollowerIds"
        @update-schema-button-clicked="useLatestCustomFieldsSchema"
        @remove-job-from-group="removeJobFromGroup"
        @unsupported-file-selected="unsupportedFileSelected"
        @error-uploading-attachment="errorUploadingAttachment"
        @show-form-submission="showFormSubmission"
        @fill-form-submission="fillFormSubmission"
        @fill-form-requirement="showFormRequirementBottomSheet"
        @view-form-requirement-submission="showFormRequirementBottomSheet"
        @show-form-selection="showFormSelection"
        @show-assignment-form-selection="showAssignmentFormSelection"
        @edit-form-submission="editFormSubmission"
        @clear-signature="clearSignature"
        @update-start-time="handleUpdateStartTime"
        @auto-fill="autoFill"
        @update-pool-execution-plan="updatePoolExecutionPlan"
        @clear-assigned-pool="clearAssignedPool"
        @update:assignment-form-requirements="updateAssignmentFormRequirements"
        @pool:closed="handlePoolClosed"
        @retry-custom-schema-fetch="fetchExtensionSchema"
      />

      <!-- Dialogs -->
      <SendEmailDialog
        v-if="jobId"
        v-model="isEmailDialogVisible"
        :persistent="true"
        :job-id="jobId"
        @set-email-dialog-visibility="setEmailDialogVisibility"
        @notification-sent="handleNotificationSent"
      />

      <template #snack-bar-wrapper>
        <vs-snackbar v-model="isSnackbarVisible" :type="snackbar.type" sticky>
          {{ snackbar.messages[snackbar.messageKey] }}
        </vs-snackbar>
      </template>

      <template v-if="showActionBar" #action-bar>
        <CombinedActionBar
          :job-id="jobId"
          :company="company"
          :is-saving="isJobSaving || isCreatingJobs"
          is-single-line
          @on-click="handleActionBarClick"
          @handle-snackbar="setSnackbarData"
        />
      </template>

      <template #bottom-sheet>
        <vs-bottom-sheet :is-visible="isBottomSheetVisible" :full-height="isBottomSheetFullHeight">
          <project-selection-page
            v-if="jobId && jobBottomSheetStates.isProjectBottomSheetVisible"
            :job-id="jobId"
            :owner-id="jobHubId"
            @job-added-success="addToProject"
            @job-added-failure="handleJobAddedFailure"
          />

          <batch-selection-page
            v-if="jobId && jobBottomSheetStates.isBatchBottomSheetVisible"
            :job-uuid="jobUuid"
            :hub-id="jobHubId"
            @job-added-success="addToBatch"
            @job-added-failure="handleJobAddedFailure"
          />

          <form-submission
            v-if="shouldShowFormSubmission"
            :form-submission-id="selectedFormSubmissionId"
            parent-job-is-approved="false"
            @dismiss="closeFormSubmission"
            @edited="updatedAttachedFormSubmission"
          />

          <form-submission
            v-else-if="shouldShowFormRequirementSubmission"
            :form-submission-id="selectedFormRequirement.formSubmissionId"
            @dismiss="closeFormRequirementBottomSheet"
            @edit="updatedFormRequirementSubmission"
          />

          <schema-based-form
            v-else-if="shouldShowFillFormSubmission || isEditingJobFormSubmission"
            :form-id="selectedFormId"
            :business-unit-id="jobHubId"
            :form-submission-id="selectedFormSubmissionId"
            @dismiss="closeSchemaBasedForm"
            @submitted="
              (e) =>
                isEditingJobFormSubmission
                  ? updatedAttachedFormSubmission()
                  : attachFormSubmission(e)
            "
          />

          <schema-based-form
            v-else-if="shouldShowFillFormRequirement"
            :form-id="selectedFormRequirement.formId"
            :business-unit-id="jobHubId"
            :form-submission-id="selectedFormSubmissionId"
            @dismiss="closeFormRequirementBottomSheet"
            @submitted="attachFormRequirement"
          />

          <new-form-selection-bottom-sheet
            v-if="isFormSelectionVisible"
            data-test="new-from-selection"
            :forms="fillableForms"
            @select="selectNewForm"
            @dismiss="closeNewFormSelection"
          />

          <new-form-selection-bottom-sheet
            v-if="isAssignmentFormSelectionVisible"
            :forms="assignmentFormRequirementOptions"
            @select="addAssignmentFormRequirement"
            @dismiss="closeAssignmentFormSelection"
          />

          <line-item-form
            v-if="jobBottomSheetStates.isLineItemBottomSheetVisible"
            :job-id="jobUuid"
            :job-number="jobId"
            :assigned-to-service-provider-id="serviceProviderBusinessUnitId"
            :hub-id="jobHubId"
            :batch-id="invoiceId"
          />

          <JobNewGuestUserForm
            v-if="jobBottomSheetStates.isNewGuestUserBottomSheetVisible"
            @user-created="(userSid) => (customerSid = userSid)"
          />

          <JobSignatureForm
            v-if="jobBottomSheetStates.isSignatureBottomSheetVisible"
            :job-id="jobUuid"
            @signature-added="addSignature"
          />

          <JobTagsEditor
            v-if="jobBottomSheetStates.isTagBottomSheetVisible"
            :job-id="isNewTicket ? undefined : jobUuid"
            @tags-updated="invalidateJobsList"
          />

          <portal-target name="job-on-the-fly-location-form-bottom-sheet" />
        </vs-bottom-sheet>
      </template>
    </FormLayout>
  </span>
</template>

<script>
import { computed, ref } from 'vue'
import { isEqual, uniq } from 'lodash'
import {
  applyFollowerChangeBySid,
  hasUntouchedLegacyFollowers,
  resolveJobFollowers,
} from '@/components/tickets/JobFollowersPicker/resolveJobFollowers'
import { ERROR_SAVING_JOB, SAVED_JOB, SAVING_JOB } from '@/store/modules/jobs'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import { v4 as uuidv4 } from 'uuid'
import moment from 'moment'
import { priorities } from '@/utils/constants'
import CombinedActionBar from '@/components/tickets/CombinedActionBar.vue'
import FormLayout from '../common/FormLayout'
import SendEmailDialog from './SendEmailDialog'
import TicketForm from './TicketForm'
import JobStatusChip from '@/components/tickets/JobStatusChip'
import JobErrorBanner from './JobErrorBanner.vue'
import JobReadonlyBanner from './JobReadonlyBanner.vue'
import FormSubmission from '../forms/FormSubmission.vue'
import {
  ASSIGNED_TO_SERVICE_PROVIDER,
  CANCELLED,
  COMPLETED,
  DRAFT,
  NO_APPROVAL,
  REQUESTED,
  SCHEDULED,
} from '@/models/job'
import ProjectSelectionPage from '../project/ProjectSelectionPage.vue'
import ProjectBreadcrumbs from '../project/ProjectBreadcrumbs.vue'
import { TASK_NOT_STARTED } from '@/models/unit_number'
import SchemaBasedForm from '@/components/forms/SchemaBasedForm'
import { getJobRequest, updateJobRequestConfiguration, useGetJobStopsQuery } from '@/api/jobs'
import { standardizeInventoryList } from '@/utils/inventories'
import NewFormSelectionBottomSheet from '@/components/forms/NewFormSelectionBottomSheet'
import {
  applyCustomDataOnCloneEffects,
  prepareCustomDataForSubmission,
} from '@/utils/field-schema-helper'
import CreatedInfo from '@/components/common/CreatedInfo.vue'
import JobAssignmentFormRequirement from '@/models/job-assignment-form-requirement'
import { useHubContextProvider } from '@/common/useHubs'
import LineItemForm from '@/components/jobs/line-items/LineItemForm.vue'
import JobNewGuestUserForm from '@/components/jobs/JobNewGuestUserForm.vue'
import JobSignatureForm from '@/components/jobs/JobSignatureForm.vue'
import BatchSelectionPage from '@/components/batches/BatchSelectionPage.vue'
import { removeJobsFromInvoice } from '@/api/invoice'
import { fetchLocationById, useGrantLocationToHub } from '@/api/locations'
import { getExtensionSchemaFormatted } from '@/api/schemas'
import { resolveEmptyFormDataForSubmission } from '@/utils/forms/form-resolver'
import { useQueryClient } from '@tanstack/vue-query'
import { mapActions as mapPiniaActions, mapState as mapPiniaState } from 'pinia'
import { useJobBottomSheetStatesStore } from '@/components/jobs/jobBottomSheetStatesStore'
import JobTagsEditor from '@/components/jobs/JobTagsEditor.vue'
import { useJobFormDataStore } from '@/components/jobs/jobFormDataStore'
import NewTag from '@/models/new_tag'
import {
  JOBS_FILTERED_COUNT_QUERY_KEY,
  JOBS_FILTERED_QUERY_KEY,
} from '../jobs/job-search-page/useFilterJobsQuery'
import { removeJobFromProjectMutation } from '../../api/useProjectsMutations'
import { JobStopType } from '@ironsight/types'
import { ActionType } from '@/components/tickets/ActionBarActions/ActionTypes'
import { snackbarMessages } from '@/components/tickets/SnackbarMessages'
import { buildRootSchema } from './common/useExtensionSchemaId'
import { useCanJobBeEdited } from '@/components/tickets/useCanJobBeEdited'
import { useParentRouteName } from '@/components/jobs/composables/useParentRouteName'
import { useGetServiceProviderCapabilities } from '@/api/companies'
import { computeFormsWithSubmissionInfo } from './JobForm'
import { BUCKET_COUNT_QUERY_KEY } from '@/components/jobs/job-search-page/useJobBucketCounts'
import { getJobSearchRouteContext } from '@/common/useJobSearchRouteContext'
import { FollowUpJobsAllowance } from '@/models/activity'

const resetTime = (hours, minutes, diff) => {
  return moment().hours(hours).minutes(minutes).add(diff, 'minutes').seconds(0).milliseconds(0)
}

export default {
  name: 'Ticket',
  components: {
    JobTagsEditor,
    JobNewGuestUserForm,
    JobSignatureForm,
    BatchSelectionPage,
    LineItemForm,
    CreatedInfo,
    NewFormSelectionBottomSheet,
    SchemaBasedForm,
    JobStatusChip,
    CombinedActionBar,
    FormLayout,
    SendEmailDialog,
    TicketForm,
    FormSubmission,
    JobReadonlyBanner,
    JobErrorBanner,
    ProjectSelectionPage,
    ProjectBreadcrumbs,
  },
  props: {
    onSaveRoute: {
      type: String,
      default: 'ticket-form',
      required: false,
    },
    id: {
      type: [String, Number],
      default: 'new',
      required: true,
    },
    hubId: {
      type: String,
      default: '',
    },
    locationId: {
      type: [String, Number],
      default: null,
    },
  },
  setup(props) {
    /*
     * "Providing" a hubId is a little tricky, since sometimes we have a hub
     * id on the props, othertimes its only available after the job is loaded.
     * We can deal with that by "providing" a ref, and then later updating the
     * ref's value once the job loads.
     */
    const contextualHubId = ref(props.hubId)
    useHubContextProvider(contextualHubId)
    const {
      mutateAsync: grantLocation,
      isLoading: grantingLocation,
      error: grantLocationError,
    } = useGrantLocationToHub()
    const queryClient = useQueryClient()

    const { mutateAsync: removeFromProject, error: removeFromProjectError } =
      removeJobFromProjectMutation()

    const contextualJobId = ref('')
    const { data: actualStops, isLoading: actualStopsLoading } =
      useGetJobStopsQuery(contextualJobId)

    const jobFormDataStore = useJobFormDataStore()
    const requestedStops = computed(() => jobFormDataStore.requestedStops)
    const destinationLocationId = computed(() => {
      const stop = jobFormDataStore.requestedStops.find((s) => s.stopType === JobStopType.DropOff)
      return stop?.locationId ?? null
    })
    const draftTags = computed(() => jobFormDataStore.draftTags)

    const canJobBeEdited = useCanJobBeEdited()
    const { parentRouteName } = useParentRouteName()

    // This is used in TaskItem.vue. We need this to load before Ticket.vue (and thus TaskItem.vue) is loaded
    const { isLoading: serviceProviderCandidatesLoading } = useGetServiceProviderCapabilities()

    const repeatJobsCount = computed(() => jobFormDataStore.repeatJobsCount)
    const isRepeatTrip = computed(() => jobFormDataStore.isRepeatTrip)

    return {
      canJobBeEdited,
      contextualHubId,
      contextualJobId,
      grantLocation,
      grantingLocation,
      grantLocationError,
      queryClient,
      removeFromProject,
      removeFromProjectError,
      requestedStops,
      actualStops,
      destinationLocationId,
      draftTags,
      parentRouteName,
      serviceProviderCandidatesLoading,
      actualStopsLoading,
      repeatJobsCount,
      isRepeatTrip,
    }
  },
  data() {
    return {
      registeredTimeouts: [],
      isCreatingJobs: false,
      isFetching: true,
      loadJobError: false,
      isEmailDialogVisible: false,
      ticket: null,
      selectedFormSubmissionId: null,
      selectedFormId: null,
      isFormSelectionVisible: false,
      selectedFormRequirement: null,
      isAssignmentFormSelectionVisible: false,
      isEditingJobFormSubmission: false,
      selectedLineItem: null,
      timeSlotDates: [],
      timeSlotStart: null,
      timeSlotDuration: 1,
      poolId: null,
      poolExecutionPlan: null,
      bulkCreate: false,
      snackbar: {
        type: 'info',
        timeout: 6000,
        messageKey: null,
        showActions: true,
        messages: snackbarMessages,
      },
      isSnackbarVisible: false,
      resourceTypeId: null,
      // Job properties
      jobId: null,
      jobUuid: null,
      jobRequestId: null,
      jobHubId: null,
      status: null,
      isArchived: false,
      approvalStatus: null,
      priorityId: 0,
      ownerId: null,
      accountId: null,
      customerSid: null,
      followerIds: [],
      // An API that predates follower_ids returns followers as sids only (a current API returns an id
      // for every follower); see resolveJobFollowers. Sids matching no known user get placeholder ids,
      // and the records a sid matches can't say which follow. Held as form state rather than read off
      // the loaded job, so a clone (which clears jobId) keeps them.
      unresolvedFollowerIds: [],
      legacyFollowerIds: {},
      loadedFollowerSidsById: {},
      startTime: null,
      endTime: null,
      activityId: null,
      crew: [],
      unitId: null,
      serviceProviderId: null,
      serviceProviderBusinessUnitId: null,
      costCenter: null,
      details: null,
      attachmentsStorageId: null,
      extensionSchemaId: null,
      customData: {},
      projectId: null,
      created: null,
      createdBy: null,
      unitWorkingStatus: null,
      activeDuration: null,
      formSubmissions: [],
      attachments: [],
      assignmentGroupNumber: null,
      BULK_JOB_LIMIT: 100,
      assignmentFormRequirements: [],
      invoiceId: null,
      signature: null,
      extensionSchema: {},
      extensionSchemaFetchError: null,
      rawRequestedStops: [],
      jobRequest: null,
    }
  },
  computed: {
    ...mapPiniaState(useJobBottomSheetStatesStore, {
      jobBottomSheetStates: 'jobBottomSheetStates',
      newIsBottomSheetVisible: 'isBottomSheetVisible',
      isBottomSheetFullHeight: 'isBottomSheetFullHeight',
    }),
    ...mapState(['initialized']),
    ...mapState('tickets', {
      ticketsError: 'error',
    }),
    ...mapState('jobs', {
      supplementaryDataLoaded: 'supplementaryDataLoaded',
    }),
    ...mapState('businessUnits', {
      allBusinessUnits: 'all',
    }),
    ...mapGetters({
      allUsers: 'users/getAllUsers',
      getJobById: 'jobs/getById',
      getTaskById: 'tasks/getById',
      unitNumbers: 'unitNumbers/getVisibleUnits',
      getDivisionByUuid: 'divisions/getByUuid',
      getUsersRelatedToBusinessUnit: 'users/getUsersRelatedToBusinessUnit',
      getJobCustomFieldsSchema: 'schemas/getJobCustomFieldsSchema',
      visibleActivities: 'activities/getVisibleActivities',
      isJobSaving: 'jobs/getSaving',
      canModifyScheduledOrInProgressJob: 'permissions/canModifyScheduledOrInProgressJob',
      canModifyRequester: 'permissions/canModifyRequester',
      getForms: 'forms/getAllVisible',
      getRolesForBusinessUnit: 'permissions/getRolesForBusinessUnit',
      getByOrganizationId: 'companies/getByOrganizationId',
      getActivityById: 'activities/getById',
      isHubUser: 'permissions/isHostOrganizationUser',
    }),
    hasBeenScheduled() {
      return !(
        this.status === DRAFT ||
        this.status === REQUESTED ||
        this.status === ASSIGNED_TO_SERVICE_PROVIDER ||
        this.status === SCHEDULED
      )
    },
    isReadonly() {
      return !this.canJobBeEdited(this.status, this.isArchived, this.jobHubId)
    },
    isCancelled() {
      return this.status === CANCELLED
    },
    loading() {
      return (
        this.isFetching ||
        !this.initialized ||
        !this.supplementaryDataLoaded ||
        this.serviceProviderCandidatesLoading ||
        (!this.isNewTicket && this.actualStopsLoading)
      )
    },
    projectSideSheetRoute() {
      const routePrefix = this.$route.name.split('-')[0]
      return `${routePrefix}-project`
    },
    batchSideSheetRoute() {
      return getJobSearchRouteContext().batchRouteName
    },
    users() {
      return this.getUsersRelatedToBusinessUnit(this.jobHubId)
    },
    titleLabel() {
      if (this.isNewTicket) {
        return this.$route.query?.fromJob
          ? `Cloned Job · ${this.company?.shortCode}`
          : `New Job · ${this.company?.shortCode}`
      } else {
        const activity = this.getActivityById(this.activityId)
        return activity?.taskName ?? ''
      }
    },
    displayedJobId() {
      return `Job #${this.$route.params.id} · ${this.company?.shortCode}`
    },
    saveButtonLabel() {
      return 'Save'
    },
    draftRepeatedJobsChangesDetected() {
      if (!this.jobRequest) {
        return this.repeatJobsCount >= 2
      }

      return (
        (!this.isRepeatedJob && this.jobRequest.maxExecutions !== this.repeatJobsCount) ||
        (!this.isRepeatedJob && this.jobRequest.isRepeatTrip !== this.isRepeatTrip)
      )
    },
    // Legacy sids sent alongside followerIds, so an API that predates follower_ids still receives
    // the followers. A multi-org user's records share one sid, hence uniq.
    followerSids() {
      // Fall back to the sid the job was loaded with for an id the users list doesn't know, including
      // the placeholder ids of unresolved legacy sids
      const sids = this.followerIds
        .map((id) => this.allUsers[id]?.id ?? this.loadedFollowerSidsById[id])
        .filter(Boolean)

      return uniq(sids)
    },
    // While followers are unresolved or untouched legacy sids, follower_ids is omitted: the API may have
    // upgraded since the job loaded, and it prefers follower_ids, so an incomplete or widened list would
    // change followers the user didn't touch. Without follower_ids it falls back to followerSids,
    // keeping the records already following for each sid sent.
    savesFollowersBySid() {
      return (
        // Placeholder ids aren't real users, so they can't be sent as follower ids
        this.followerIds.some((id) => this.unresolvedFollowerIds.includes(id)) ||
        hasUntouchedLegacyFollowers(this.followerIds, this.legacyFollowerIds, this.allUsers)
      )
    },
    followerIdsToSave() {
      return this.savesFollowersBySid ? undefined : this.followerIds
    },
    draftChangesDetected() {
      if (this.loading || this.loadJobError || this.showReadonlyJob) {
        return false
      }

      const job = this.getJobById(this.jobId)
      if (!job && this.formIsEmpty) return false
      if (!job && !this.formIsEmpty) return true

      return (
        this.extensionSchemaId !== job.extensionSchemaId ||
        this.priorityId !== job.priorityId ||
        this.ownerId !== job.ownerId ||
        this.customerSid !== job.customerSid ||
        this.activityId !== job.activityId ||
        this.unitId !== job.unitId ||
        this.serviceProviderBusinessUnitId !== job.serviceProviderBusinessUnitId ||
        this.poolId !== job.poolId ||
        this.poolExecutionPlan != null ||
        this.costCenter !== job.costCenter ||
        this.details !== job.details ||
        this.resourceTypeId !== job.resourceTypeId ||
        !this.startTime.isSame(job.startTime) ||
        !this.endTime.isSame(job.endTime) ||
        this.draftRepeatedJobsChangesDetected ||
        !isEqual(this.crew, job.crew) ||
        !isEqual(this.followerIds, resolveJobFollowers(job, this.allUsers).followerIds) ||
        !isEqual(
          job.requestedStops
            .sort((a, b) => a.position - b.position)
            .map((rs) => ({
              locationId: rs.locationId,
              inventory: standardizeInventoryList(
                rs.inventory.map((ri) => ({
                  quantity: ri.quantity,
                  inventorySubTypeId: ri.inventorySubTypeId,
                })),
              ),
            })),
          this.requestedStops.map((i) => ({
            locationId: i.locationId,
            inventory: standardizeInventoryList(
              i.inventory.map((ri) => ({
                quantity: ri.quantity,
                inventorySubTypeId: ri.inventorySubTypeId,
              })),
            ),
          })),
        ) ||
        !isEqual(
          // The existing job.customData from the server is already "sanitized". Therefore, before
          // comparing to the server data, we also need to sanitize the data
          resolveEmptyFormDataForSubmission(this.customData),
          // Job's custom data created by custom integrations are not sanitized. Therefore, we also
          // need to sanitize the server data before doing the comparison
          resolveEmptyFormDataForSubmission(job.customData),
        ) ||
        !isEqual(this.assignmentFormRequirements, job.assignmentFormRequirements)
      )
    },
    formIsEmpty() {
      return !(
        this.priorityId !== 1 ||
        this.ownerId ||
        this.customerSid ||
        this.activityId ||
        this.unitId ||
        this.serviceProviderBusinessUnitId ||
        this.costCenter ||
        this.details ||
        this.resourceTypeId ||
        this.repeatJobsCount >= 2 ||
        this.crew.length > 0 ||
        this.followerIds.length > 0 ||
        this.requestedStops.length > 1 ||
        Object.keys(this.customData).length > 0 ||
        this.draftTags.length > 0
      )
    },
    isNewTicket() {
      return this.id === 'new'
    },
    allowEditingFollowUpSection() {
      return (
        this.repeatJobsCount >= 2 &&
        this.isNewTicket &&
        this.activity?.followUpJobsAllowance !== FollowUpJobsAllowance.NotAllowed
      )
    },
    showActionBar() {
      return !this.isNewTicket
    },
    canDiscard() {
      return this.isNewTicket ? null : 'on-discard'
    },
    startEndDuration() {
      return moment.duration(this.endTime.diff(this.startTime)).asHours()
    },
    isBottomSheetVisible() {
      return (
        !!this.selectedFormSubmissionId ||
        !!this.selectedFormId ||
        this.isFormSelectionVisible ||
        this.isAssignmentFormSelectionVisible ||
        !!this.selectedFormRequirement ||
        this.newIsBottomSheetVisible
      )
    },
    activity() {
      return this.getActivityById(this.activityId)
    },
    formsWithSubmissionInfo() {
      return computeFormsWithSubmissionInfo(
        this.formSubmissions,
        this.getActivityById(this.activityId).formAssociations,
      )
    },
    shouldShowFillFormSubmission() {
      return this.jobId && this.selectedFormId
    },
    shouldShowFormSubmission() {
      return (
        this.jobId &&
        this.selectedFormId &&
        this.selectedFormSubmissionId &&
        !this.isEditingJobFormSubmission
      )
    },
    shouldShowFormRequirementSubmission() {
      return (
        !this.isNewTicket &&
        this.selectedFormRequirement &&
        this.selectedFormRequirement.formSubmissionId !== null
      )
    },
    shouldShowFillFormRequirement() {
      return (
        !this.isNewTicket &&
        this.selectedFormRequirement &&
        this.selectedFormRequirement.formSubmissionId === null
      )
    },
    finalStatus() {
      return this.approvalStatus !== NO_APPROVAL ? this.approvalStatus : this.status.trim()
    },
    fillableForms() {
      return this.getForms
        .filter((f) => f.fillable)
        .filter((f) => {
          const accessGrant = f.accessGrants.find((ag) => ag.businessUnitId === this.jobHubId)
          if (!accessGrant) return false

          const userRoles = this.getRolesForBusinessUnit(this.jobHubId)

          return userRoles.some((role) => !accessGrant.roleRestrictions.includes(role))
        })
    },
    assignmentFormRequirementOptions() {
      return this.getForms
        .filter((f) => f.fillable)
        .filter((f) => {
          const businessUnitId =
            this.company.organizationId === this.serviceProviderBusinessUnitId
              ? this.jobHubId
              : this.serviceProviderBusinessUnitId
          const accessGrant = f.accessGrants.find((ag) => ag.businessUnitId === businessUnitId)
          if (!accessGrant) return false

          const userRoles = this.getRolesForBusinessUnit(businessUnitId)
          return userRoles.some((role) => !accessGrant.roleRestrictions.includes(role))
        })
    },
    company() {
      const hub = this.allBusinessUnits[this.jobHubId]
      return this.getByOrganizationId(hub?.ownerId)
    },
    // TODO(aaron): refactor out to separate component.
    // Most of this should pushed down to TicketForm. Ticket should only care
    // about extensionSchemaId, if even that.
    /**
     * After an Activity is selected by the user (or existing job), this will
     * populate with the latest extension schema ID.
     */
    customFieldsLatestIdForActivity() {
      if (this.activityId) {
        const activity = this.getActivityById(this.activityId)
        return this.getTaskById(activity?.taskId)?.version
      }

      return null
    },
    /**
     * Only way the IDs mismatch is if a newer custom fields schema is available.
     */
    customFieldsNewerAvailable() {
      return this.extensionSchemaId !== this.customFieldsLatestIdForActivity
    },
    /**
     * A merge of the activity, division, and global defs into 1 custom fields
     * JSON schema.
     */
    customFieldsSchema() {
      return buildRootSchema(
        this.getDivisionByUuid(this.ownerId),
        this.getTaskById(this.getActivityById(this.activityId)?.taskId),
        this.getJobCustomFieldsSchema,
        this.extensionSchemaId,
        this.extensionSchema,
      )
    },
    combinedStops() {
      return {
        requested: this.rawRequestedStops,
        actual: this.actualStops,
      }
    },
    isRepeatedJob() {
      return this.jobUuid && this.jobRequest && this.jobRequest.primaryJobId !== this.jobUuid
    },
    showRepeatedJobWarningBanner() {
      // you had follow-ups enabled and chose an activity without them
      const disabledFollowups =
        this.jobRequestId &&
        this.activity?.followUpJobsAllowance === FollowUpJobsAllowance.NotAllowed

      // you had multi-service enabled and chose an activity that doesn't support it
      const disabledMultiService =
        this.jobRequest?.isRepeatTrip === false &&
        this.activity &&
        this.activity?.followUpJobsAllowance !== FollowUpJobsAllowance.MultiService

      return !this.isNewTicket && !this.isRepeatedJob && (disabledFollowups || disabledMultiService)
    },
  },
  watch: {
    extensionSchemaId(newId, oldId) {
      // Ensures that the correct schema is loaded set based on if:
      // - The job is exists then use the schema based on its extension id.
      // - The job is cloned then use the latest schema available.
      // - The job's extension id is updated to use the latest schema available.
      if (newId !== oldId) {
        this.fetchExtensionSchema()
      }
    },
    combinedStops(newStops) {
      let toSet = newStops.requested
      if (newStops.actual) {
        toSet = newStops.requested.map((x) => ({
          ...x,
          isDisabled:
            (x.stopType === JobStopType.PickUp &&
              newStops.actual.some((s) => s.requestedStopId === x.id)) ||
            (x.stopType === JobStopType.DropOff &&
              newStops.actual.some((s) => s.stopType === JobStopType.DropOff)),
        }))
      }
      this.setRequestedStops(toSet)
    },
  },
  beforeDestroy() {
    this.clearRegisteredTimeouts()
    this.isSnackbarVisible = false
    this.extensionSchema = {}
    this.customData = {}
    this.formSubmissions = {}
  },
  async mounted() {
    if (this.id === 'new') {
      if (this.$route.query?.fromJob) {
        const job = this.getJobById(this.$route.query?.fromJob)
        await this.setJobProperties(job)
        const hadFollowUpJobsEnabled =
          this.jobRequest?.maxExecutions >= 2 || !!this.jobRequest?.isRepeatTrip
        await this.resetCloneJob(job)
        this.setSnackbarData({
          messageKey: hadFollowUpJobsEnabled ? 'cloneFollowUpJobsDisabled' : 'clone',
        })
      } else {
        this.initializeJobProperties()
      }
    } else {
      const job = this.getJobById(this.id)
      this.setJobProperties(job)
    }
    this.isFetching = false
  },
  methods: {
    registerTimeout(fn, ms) {
      const id = setTimeout(fn, ms)
      this.registeredTimeouts.push(id)
      return id
    },
    clearRegisteredTimeouts() {
      if (!this.registeredTimeouts) return
      this.registeredTimeouts.forEach((id) => clearTimeout(id))
      this.registeredTimeouts = []
    },
    ...mapPiniaActions(useJobFormDataStore, {
      setRequestedStops: 'setRequestedStops',
      setRepeatJobsCount: 'setRepeatJobsCount',
      setRepeatJobsCountInitialValue: 'setRepeatJobsCountInitialValue',
      setIsRepeatTrip: 'setIsRepeatTrip',
      setIsRepeatTripInitialValue: 'setIsRepeatTripInitialValue',
    }),
    ...mapActions('jobs', [
      'createJobs',
      'updateJob',
      'updateJobStatus',
      'archiveJob',
      'unarchiveJob',
      'fetchJob',
      'updatePriority',
      'attachFormSubmissionId',
      'attachFormRequirementSubmission',
      'removeJobFromAssignmentGroup',
      'addAttachment',
      'removeJobFrom',
    ]),
    ...mapMutations({
      removeJobFromStore: 'jobs/REMOVE_JOB',
      removeJobFromProjectStore: 'jobs/REMOVE_FROM_PROJECT',
    }),
    ...mapMutations('jobs', {
      savingJob: SAVING_JOB,
      savedJob: SAVED_JOB,
      errorSavingJob: ERROR_SAVING_JOB,
    }),
    setFollowerIds(followerIds) {
      // A save by sid can't keep a choice between a multi-org user's records, so edit per person until
      // the job is loaded with follower ids
      this.followerIds = this.savesFollowersBySid
        ? applyFollowerChangeBySid(this.followerIds, followerIds, this.allUsers)
        : followerIds
    },
    initializeJobProperties() {
      const nextInterval = Math.ceil(moment().add(1, 'minute').minute() / 15) * 15 // 15, 30, 45, or 60
      const defaultStart = moment().startOf('hour').add(nextInterval, 'minutes')
      const defaultEnd = moment().startOf('hour').add(nextInterval, 'minutes').add(1, 'hour')

      this.jobUuid = uuidv4()
      this.status = DRAFT
      this.approvalStatus = NO_APPROVAL
      this.unitWorkingStatus = TASK_NOT_STARTED
      this.priorityId = priorities.filter((p) => p.name === 'New')[0].id
      this.ownerId = null
      this.customerSid = null
      this.followerIds = []
      this.unresolvedFollowerIds = []
      this.legacyFollowerIds = {}
      this.loadedFollowerSidsById = {}
      this.activityId = null
      this.crew = []
      this.unitId = null
      this.serviceProviderId = null
      this.serviceProviderBusinessUnitId = null
      this.costCenter = ''
      this.details = ''
      this.attachmentsStorageId = null
      this.extensionSchemaId = null
      this.customData = {}
      this.projectId = null
      this.created = moment().toISOString()
      this.jobHubId = this.hubId
      this.assignmentGroupNumber = null
      this.invoiceId = null

      this.rawRequestedStops = [
        {
          _uniqueId: uuidv4(),
          id: null,
          locationId: this.locationId,
          inventory: [],
          stopType: JobStopType.DropOff,
          isDisabled: false,
        },
      ]

      if (!this.canModifyRequester) {
        this.autoFill('requestor', this.$auth.user.sub)
      }
      this.autoFill('start', defaultStart)
      this.autoFill('end', defaultEnd)
    },
    async setJobProperties(job, forceFetchJobRequest = false) {
      this.jobId = job.id
      this.jobUuid = job.uuid
      this.jobRequestId = job.jobRequestId
      this.status = job.status
      this.isArchived = job.isArchived
      this.approvalStatus = job.approvalStatus
      this.unitWorkingStatus = job.unitWorkingStatus
      this.priorityId = job.priorityId
      this.ownerId = job.ownerId
      this.customerSid = job.customerSid
      const followers = resolveJobFollowers(job, this.allUsers)
      this.followerIds = followers.followerIds
      this.unresolvedFollowerIds = followers.unresolvedIds
      this.legacyFollowerIds = followers.legacyIds
      this.loadedFollowerSidsById = followers.sidsById
      this.startTime = job.startTime
      this.endTime = job.endTime
      this.crew = job.crew
      this.unitId = job.unitId
      this.serviceProviderBusinessUnitId = job.serviceProviderBusinessUnitId
      this.poolId = job.poolId
      this.poolExecutionPlan = job.poolExecutionPlan
      this.resourceTypeId = job.resourceTypeId
      this.costCenter = job.costCenter
      this.details = job.details
      this.attachmentsStorageId = job.attachmentsStorageId
      this.extensionSchemaId = job.extensionSchemaId
      this.customData = job.customData
      this.projectId = job.projectId
      this.created = job.created
      this.createdBy = job.createdBy
      this.activeDuration = job.actualActiveDuration
      this.formSubmissions = job.formSubmissions
      this.attachments = job.attachments
      this.jobHubId = job.hubId
      this.assignmentGroupNumber = job.assignmentGroupNumber
      this.assignmentFormRequirements = job.assignmentFormRequirements
      this.invoiceId = job.invoiceId
      this.signature = job.signature
      this.accountId = job.accountId

      this.contextualHubId = job.hubId
      this.contextualJobId = job.uuid

      this.rawRequestedStops = job.requestedStops
        .sort((a, b) => a.position - b.position)
        .map((x) => ({
          ...x,
          _uniqueId: uuidv4(),
          inventory: x.inventory.map((y) => ({
            ...y,
            _uniqueId: uuidv4(),
          })),
          isDisabled: false,
        }))
      this.activityId = job.activityId

      await this.fetchJobRequest(forceFetchJobRequest)
      if (this.jobRequestId && this.jobRequest.primaryJobId === this.jobUuid) {
        this.setRepeatJobsCount(this.jobRequest.maxExecutions)
        this.setRepeatJobsCountInitialValue(this.jobRequest.maxExecutions)
        this.setIsRepeatTrip(this.jobRequest.isRepeatTrip)
        this.setIsRepeatTripInitialValue(this.jobRequest.isRepeatTrip)
      } else {
        this.setRepeatJobsCount(1)
        this.setRepeatJobsCountInitialValue(1)
        this.setRepeatJobsCount(1)
        this.setRepeatJobsCountInitialValue(1)
        this.setIsRepeatTrip(false)
        this.setIsRepeatTripInitialValue(false)
      }
    },
    async fetchJobRequest(force = false) {
      // Set jobRequest given jobRequestId. Cache for better performance.
      if (!this.jobRequestId) {
        this.jobRequest = null
        return
      }
      if (this.jobRequestId && (force || this.jobRequest?.id !== this.jobRequestId)) {
        try {
          const jobRequest = await this.queryClient.fetchQuery({
            queryKey: ['job-request', 'byId', this.jobRequestId],
            queryFn: () => getJobRequest(this.jobRequestId),
          })
          this.jobRequest = jobRequest
        } catch (e) {
          this.setSnackbarData({
            type: 'error',
            messageKey: 'fetchJobRequestError',
          })
          this.isSnackbarVisible = true
        }
      }
    },
    async fetchExtensionSchema() {
      this.extensionSchemaFetchError = null

      if (this.extensionSchemaId) {
        try {
          this.extensionSchema = await this.queryClient.fetchQuery({
            queryKey: ['schemas', 'extensionSchema', this.extensionSchemaId],
            queryFn: async () => await getExtensionSchemaFormatted(this.extensionSchemaId),
          })
        } catch (error) {
          // error message rendered in TicketForm.vue
          this.extensionSchemaFetchError = error
        }
      }
    },
    invalidateJobsList() {
      const queryKeySet = new Set([
        JOBS_FILTERED_QUERY_KEY,
        JOBS_FILTERED_COUNT_QUERY_KEY,
        BUCKET_COUNT_QUERY_KEY,
        'jobs',
        'projects',
      ])

      this.queryClient.invalidateQueries({
        predicate: (query) => queryKeySet.has(query.queryKey[0]),
      })
    },
    /**
     * Forward-fills the extensionSchemaId even if feature flag is off.
     * If flag is on, also notifies the user when their fields have updated.
     */
    async useLatestCustomFieldsSchema(hideSnackbarMessage = false) {
      if (this.customFieldsNewerAvailable && !hideSnackbarMessage) {
        this.setSnackbarData({
          type: 'warn',
          messageKey: 'versionUpdate',
        })
      }

      // We always want to attempt to fill this (even with flag off) so we can forward-fill
      // the database in prep for the flag to be enabled.
      this.extensionSchemaId = this.customFieldsLatestIdForActivity

      await this.fetchExtensionSchema()
    },
    async resetCloneJob(job) {
      // When cloning job, clear all archived or unavailable locations
      const clearStopLocation = (stop) => {
        stop.locationId = null
        stop.locationName = null
        stop.locationOwnerId = null
      }

      const handleMissingLocationError = (error, onMissing) => {
        const status = error?.response?.status
        const STATUS_CODES = [403, 404]

        if (STATUS_CODES.includes(status)) {
          onMissing()
        } else {
          throw error
        }
      }

      await Promise.all(
        job.requestedStops
          .filter((stop) => stop.locationId != null)
          .map(async (stop) => {
            try {
              const location = await fetchLocationById(this.queryClient, stop.locationId)

              if (location.isArchived) {
                clearStopLocation(stop)
              }
            } catch (error) {
              handleMissingLocationError(error, () => clearStopLocation(stop))
            }
          }),
      )

      this.setRepeatJobsCount(1)
      this.setRepeatJobsCountInitialValue(1)
      this.setIsRepeatTrip(false)
      this.setIsRepeatTripInitialValue(false)

      const timeDiff = (new Date(job.endTime) - new Date(job.startTime)) / (1000 * 60)
      const hours = moment(job.startTime).hours()
      const minutes = moment(job.startTime).minutes()
      const current = moment().toISOString()

      this.jobId = null
      this.jobUuid = uuidv4()
      this.isArchived = false
      this.approvalStatus = NO_APPROVAL
      this.unitWorkingStatus = null
      this.priorityId = priorities.find((p) => p.name === 'New').id
      this.created = current
      this.modified = current
      this.serviceProviderBusinessUnitId = null
      this.unitId = null
      this.crew = []
      this.attachmentsStorageId = null
      this.attachments = []
      this.projectId = this.$route.params.projectId
      this.assignmentGroupNumber = null
      this.formSubmissions = []
      this.poolId = null
      this.poolExecutionPlan = null
      this.assignmentFormRequirements = []
      this.signature = null
      this.accountId = null
      this.contextualJobId = null
      this.jobRequestId = null

      // Clear requested stop Ids during clone to avoid conflicts when saving
      this.rawRequestedStops.forEach((stop) => (stop.id = null))

      await this.useLatestCustomFieldsSchema(true)

      if (!this.visibleActivities.map((a) => a.id).includes(this.activityId)) {
        this.activityId = null
        this.customData = {}
      } else if (this.customFieldsSchema.uiSchema) {
        this.customData = applyCustomDataOnCloneEffects(
          this.customFieldsSchema.uiSchema['ui:onClone'],
          this.customData,
        )
      }

      this.status = DRAFT

      if (!this.canModifyRequester) {
        this.autoFill('requestor', this.$auth.user.sub)
      }

      this.autoFill(
        'start',
        current >= moment(job.startTime)._i ? resetTime(hours, minutes, 0) : job.startTime,
      )
      this.autoFill(
        'end',
        current >= moment(job.startTime)._i ? resetTime(hours, minutes, timeDiff) : job.endTime,
      )
    },
    async saveTicket() {
      if (this.failsFieldValidation(this.status)) {
        this.setSnackbarData({ type: 'error', messageKey: 'validationError' })
        return
      }
      if (this.extensionSchemaFetchError) {
        this.setSnackbarData({
          type: 'error',
          messageKey: 'failedToFetchSchema',
        })
        return
      }
      this.prepareCurrentDataForSubmission()

      if (this.isNewTicket) {
        this.isCreatingJobs = true
        await this.handleCreate(this.getStartTimes(), this.getDuration())
      } else {
        await this.handleUpdate()
      }
    },
    failsFieldValidation(jobStatus) {
      return !this.$refs['ticket-form'].validate(jobStatus)
    },
    prepareCurrentDataForSubmission() {
      this.customData = prepareCustomDataForSubmission(this.customFieldsSchema, this.customData)
    },
    async handleCreate(startTimes, duration) {
      if (this.isInvalidBulkCreate()) return

      const response = await this.createJobs({
        ...this.getCreateData(),
        startTimes,
        duration,
      })
      if (this.bulkCreate) {
        // If this is NOT a bulk create, then the job is fetched to Vuex in beforeRouteEnter() above
        await Promise.all(response.job_ids.map((id) => this.fetchJob(id)))
      } else if (!this.ticketsError) {
        // If this is NOT a bulk create AND the job was successfully created, then we upload attachments
        await Promise.all(
          this.attachments.map((attachment) =>
            this.addAttachment({
              jobId: this.jobUuid,
              attachmentId: attachment.id,
              file: attachment.tempFile,
            }),
          ),
        )
      }
      await this.grantLocationsToHub()

      this.handleCreateResponse(response)
    },
    isInvalidBulkCreate() {
      if (this.bulkCreate && this.timeSlotDates.length === 0) {
        this.setSnackbarData({
          type: 'error',
          messageKey: 'bulkCreateNoDateError',
        })
        return true
      } else if (this.bulkCreate && this.timeSlotDates.length > this.BULK_JOB_LIMIT) {
        this.setSnackbarData({
          type: 'error',
          messageKey: 'bulkCreateLimitError',
        })
        return true
      }
      return false
    },
    getCreateData() {
      return {
        jobUuid: this.bulkCreate ? null : this.jobUuid,
        activityId: this.activityId,
        attachmentsStorageId: this.attachmentsStorageId,
        costCenter: this.costCenter,
        createProject: this.bulkCreate,
        crew: this.crew,
        extensionSchemaId: this.extensionSchemaId,
        customData: this.customData,
        customerSid: this.customerSid,
        details: this.details,
        ownerId: this.ownerId,
        followers: this.followerSids,
        followerIds: this.followerIdsToSave,
        requestedStops: this.requestedStops,
        priorityId: this.priorityId,
        projectId: this.$route.params.projectId,
        repeatJobsCount: this.allowEditingFollowUpSection ? this.repeatJobsCount : null,
        isRepeatTrip: this.allowEditingFollowUpSection ? this.isRepeatTrip : false,
        serviceProviderBusinessUnitId: this.serviceProviderBusinessUnitId,
        tags: this.draftTags.map((tag) => NewTag.toObject(tag).toDisplayString),
        unitId: this.unitId,
        poolExecutionPlan: this.poolExecutionPlan,
        assignmentFormRequirements: this.assignmentFormRequirements,
      }
    },
    getStartTimes() {
      return this.bulkCreate
        ? this.timeSlotDates.sort().map((date) => moment(`${date} ${this.timeSlotStart}`))
        : [this.startTime]
    },
    getDuration() {
      return this.bulkCreate
        ? this.timeSlotDuration * 3600
        : moment.duration(this.endTime.diff(this.startTime)).asSeconds()
    },
    handleCreateResponse(response) {
      if (!this.ticketsError) {
        this.invalidateJobsList()
        this.onCreateSuccess(response)
      } else {
        this.onCreateFailure()
        this.isCreatingJobs = false
      }
    },
    onCreateSuccess(response) {
      if (this.bulkCreate) {
        this.$router.push({
          name: this.projectSideSheetRoute,
          params: { projectId: response.project_id, isJobSaved: true },
        })
      } else {
        this.$router.push({
          name: this.onSaveRoute,
          params: { id: response.job_ids[0], isJobNew: true },
        })

        this.registerTimeout(() => this.setSnackbarData({ messageKey: 'saved' }), 250)
      }
    },
    onCreateFailure() {
      if (this.bulkCreate) {
        this.setSnackbarData({
          type: 'error',
          messageKey: 'bulkCreateSaveError',
        })
      } else {
        this.setSnackbarData({
          type: 'error',
          messageKey: 'genericError',
        })
      }
    },
    async grantLocationsToHub() {
      if (this.isHubUser) return
      try {
        await Promise.all(
          this.requestedStops
            .filter((inv) => inv.locationId != null)
            .map((inv) =>
              this.grantLocation({
                locationId: inv.locationId,
                hubId: this.jobHubId,
              }),
            ),
        )
      } catch (e) {
        this.registerTimeout(
          () =>
            this.setSnackbarData({
              type: 'error',
              messageKey: 'grantLocationsToHubError',
            }),
          250,
        )
      }
    },
    async handleUpdate() {
      await this.updateJob(this.getUpdateData())

      await this.grantLocationsToHub()
      if (this.draftRepeatedJobsChangesDetected) {
        try {
          await updateJobRequestConfiguration(this.jobUuid, this.repeatJobsCount, this.isRepeatTrip)
        } catch (e) {
          this.setSnackbarData({
            type: 'error',
            messageKey: 'updateRepeatJobsCountError',
          })
        }
      }

      this.handleUpdateResponse()
    },
    getUpdateData() {
      if (this.extensionSchemaId === null) {
        // Forward-filling needed if updating Job records created prior to
        // mid-2024. Those jobs did not track their schema ID.
        this.extensionSchemaId = this.customFieldsLatestIdForActivity
      }

      return {
        id: this.jobId,
        priorityId: this.priorityId,
        ownerId: this.ownerId,
        customerSid: this.customerSid,
        followers: this.followerSids,
        followerIds: this.followerIdsToSave,
        startTime: this.startTime,
        endTime: this.endTime,
        activityId: this.activityId,
        requestedStops: this.requestedStops,
        crew: this.crew,
        unitId: this.unitId,
        serviceProviderBusinessUnitId: this.serviceProviderBusinessUnitId,
        costCenter: this.costCenter,
        details: this.details,
        extensionSchemaId: this.extensionSchemaId,
        customData: this.customData,
        poolId: this.poolId,
        poolExecutionPlan: this.poolExecutionPlan,
        assignmentFormRequirements: this.assignmentFormRequirements,
      }
    },
    async handleUpdateResponse() {
      if (!this.ticketsError) {
        await this.fetchJob(this.jobId)
        this.setJobProperties(this.getJobById(this.jobId), true)
        this.$emit('job-saved', { jobNumber: this.jobId })
        this.registerTimeout(() => this.setSnackbarData({ messageKey: 'saved' }), 250)

        const filters = { queryKey: ['line-items', this.jobUuid] }
        const cachedLineItems = this.queryClient.getQueriesData(filters).find((x) => {
          const key = x[0]
          return (
            key.length === 2 && key[0] === filters.queryKey[0] && key[1] === filters.queryKey[1]
          )
        })
        if (cachedLineItems && cachedLineItems.length === 2 && cachedLineItems[1]?.length > 0) {
          await this.queryClient.invalidateQueries(filters)
        }

        this.queryClient.invalidateQueries({
          queryKey: ['requested-stops', 'byJobId', this.jobUuid],
        })

        await this.setJobHistory()
        this.invalidateJobsList()
      } else {
        this.setSnackbarData({
          type: 'error',
          messageKey: 'genericError',
        })
      }
    },
    async updateStatus(jobStatus) {
      if (!this.jobId) return
      if (jobStatus === COMPLETED && !this.formsWithSubmissionInfo.every((f) => f.isFilledOut)) {
        this.setSnackbarData({
          messageKey: 'ironSightFormsNotAllFilled',
          type: 'error',
        })
        return
      }
      if (this.failsFieldValidation(jobStatus)) {
        return
      }

      await this.updateJobStatus({ id: this.jobId, jobStatus })

      if (!this.ticketsError) {
        this.registerTimeout(() => this.setSnackbarData({ messageKey: 'statusUpdated' }), 250)
        this.setJobProperties(this.getJobById(this.jobId))
        this.setJobHistory()
        this.$emit('job-saved', { jobNumber: this.jobId })
        this.invalidateJobsList()
      }
    },
    async archive() {
      await this.archiveJob({ uuid: this.jobUuid })

      if (!this.ticketsError) {
        this.registerTimeout(() => this.setSnackbarData({ messageKey: 'archived' }), 250)
        this.setJobProperties(this.getJobById(this.jobId))
        this.invalidateJobsList()
      }
    },
    async unarchive() {
      await this.unarchiveJob({ uuid: this.jobUuid })

      if (!this.ticketsError) {
        this.registerTimeout(() => this.setSnackbarData({ messageKey: 'unarchived' }), 250)
        this.setJobProperties(this.getJobById(this.jobId))
        this.invalidateJobsList()
      }
    },
    async cloneTicket() {
      this.$router.push({
        name: this.$route.meta?.cloneRouteName ?? this.onSaveRoute,
        params: {
          id: 'new',
          hubId: this.jobHubId,
        },
        query: { fromJob: this.jobId },
      })
    },
    handleBackToReadonly() {
      this.$emit('back-to-readonly')
    },
    dismissClicked() {
      this.$emit('dismiss')
    },
    handleDiscard() {
      const job = this.getJobById(this.jobId)
      this.setJobProperties(job)
      this.$refs['ticket-form'].handleDiscard()
      this.$nextTick(() => {
        // these values get modified by reactive watchers triggered by
        // this.setJobProperties(job)
        // so they need to be overwritten again

        // From James: This is a code smell. I suspect (not 100%) the reason we do this is because
        // in CostCenterField.vue, we have a watcher on "suggestedCostCenters".
        // When calling this.setJobProperties(job), we change the divisionId (ownerId),
        // which triggers the watcher in CostCenterField.vue, which updates
        // the suggestedCostCenters.
        // TODO: Investigate this to avoid the need for this.$nextTick hack.
        this.costCenter = job.costCenter
      })
    },
    handleUpdateStartTime(newStart) {
      if (this.endTime.isValid()) {
        this.endTime = moment(newStart).add(this.startEndDuration, 'h')
      }
      this.startTime = newStart
    },
    autoFill(field, value) {
      switch (field) {
        case 'division':
          this.ownerId = value
          break
        case 'requestor':
          this.customerSid = value
          break
        case 'resource-type':
          this.resourceTypeId = value
          break
        case 'activity':
          this.activityId = value
          this.extensionSchemaId = this.customFieldsLatestIdForActivity
          break
        case 'service-provider':
          this.serviceProviderBusinessUnitId = value
          break
        case 'start':
          this.startTime = value
          break
        case 'end':
          this.endTime = value
          break
        case 'cost-center':
          this.costCenter = value
          break
      }
    },
    closeFormSubmission() {
      this.selectedFormSubmissionId = null
      this.selectedFormId = null
    },
    closeSchemaBasedForm() {
      this.selectedFormId = null
      this.selectedFormSubmissionId = null
      this.isEditingJobFormSubmission = false
    },
    closeFormRequirementBottomSheet() {
      this.selectedFormRequirement = null
    },
    showFormSubmission(formId, formSubmissionUuid) {
      this.selectedFormSubmissionId = formSubmissionUuid
      this.selectedFormId = formId
    },
    fillFormSubmission(formUuid) {
      if (!this.jobId) {
        return
      }

      this.selectedFormId = formUuid
    },
    editFormSubmission(formUuid, formSubmissionId) {
      this.selectedFormId = formUuid
      this.selectedFormSubmissionId = formSubmissionId
      this.isEditingJobFormSubmission = true
    },
    showFormSelection() {
      this.isFormSelectionVisible = true
    },
    closeNewFormSelection() {
      this.isFormSelectionVisible = false
    },
    selectNewForm({ formId }) {
      this.isFormSelectionVisible = false
      this.selectedFormId = formId
    },
    showFormRequirementBottomSheet(formRequirement) {
      this.selectedFormRequirement = formRequirement
    },
    showAssignmentFormSelection() {
      this.isAssignmentFormSelectionVisible = true
    },
    closeAssignmentFormSelection() {
      this.isAssignmentFormSelectionVisible = false
    },
    addAssignmentFormRequirement(requirement) {
      this.isAssignmentFormSelectionVisible = false
      this.assignmentFormRequirements = [
        ...this.assignmentFormRequirements,
        JobAssignmentFormRequirement.fromFormId(requirement.formId),
      ]
    },
    async handleActionBarClick(data) {
      switch (data.type) {
        case ActionType.SEND_NOTIFICATION:
          this.setEmailDialogVisibility(true)
          break
        case ActionType.FOCUS_RESOURCE:
          this.$refs['ticket-form'].$refs['task-item'].$refs.resourceAssignment.focus()
          break
        case ActionType.CLONE:
          return this.cloneTicket()
        case ActionType.STATUS_CHANGE:
          await this.updateStatus(data.value)
          break
        case ActionType.REMOVE_FROM_PROJECT:
          await this.removeJobFromProject()
          break
        case ActionType.VIEW_PROJECT:
          this.pushToProject(this.projectId)
          break
        case ActionType.REMOVE_FROM_BATCH:
          await this.removeJobFromBatch()
          break
        case ActionType.VIEW_BATCH:
          this.pushToBatch(this.invoiceId)
          break
        case ActionType.ARCHIVE:
          await this.archive()
          this.$emit('job-saved', { jobNumber: this.jobId })
          break
        case ActionType.UNARCHIVE:
          await this.unarchive()
          this.$emit('job-saved', { jobNumber: this.jobId })
          break
        default:
          break
      }
      data.complete()
    },
    unsupportedFileSelected(messageKey) {
      this.setSnackbarData({ type: 'error', messageKey })
    },
    errorUploadingAttachment(messageKey) {
      this.setSnackbarData({ type: 'error', messageKey })
    },
    async addToBatch(batchId) {
      this.invoiceId = batchId
      this.pushToBatch(batchId)
      this.$emit('job-saved', { jobNumber: this.jobId })
      await this.fetchJob(this.jobId)
    },
    async removeJobFromBatch() {
      try {
        this.savingJob()
        await removeJobsFromInvoice(this.invoiceId, [this.jobUuid])
        await this.fetchJob(this.jobId)
        this.invoiceId = null
        this.setSnackbarData({
          type: 'info',
          messageKey: 'removeJobFromBatchSuccess',
        })
        this.$emit('job-saved', { jobNumber: this.jobId })
        this.savedJob()
      } catch (e) {
        this.setSnackbarData({
          type: 'error',
          messageKey: 'removeJobFromBatchError',
        })
        this.errorSavingJob()
      }
    },
    async addToProject(projectId) {
      this.projectId = projectId

      this.pushToProject(this.projectId)

      this.$emit('job-saved', { jobNumber: this.jobId })

      this.invalidateJobsList()
    },
    async removeJobFromProject() {
      const { projectId, jobId } = this
      await this.removeFromProject({ projectId, jobNumber: jobId })

      if (this.removeFromProjectError) {
        this.setSnackbarData({
          type: 'error',
          messageKey: 'removeJobFromProjectError',
        })
      } else {
        this.projectId = null
        this.$emit('job-saved', { jobNumber: this.jobId })
      }

      this.invalidateJobsList()

      // Remove the button from the ActionBar does not actually
      // interact with the store.
      this.removeJobFromProjectStore(this.jobId)
    },
    pushToProject(projectId) {
      this.$router.push({
        name: this.projectSideSheetRoute,
        params: { projectId },
      })
    },
    pushToBatch(batchId) {
      this.$router.push({
        name: this.batchSideSheetRoute,
        params: { batchId, parentRouteName: this.parentRouteName },
      })
    },
    handleNotificationSent() {
      this.setSnackbarData({ messageKey: 'notificationSent' })
    },
    handleJobAddedFailure(messageKey) {
      this.setSnackbarData({
        type: 'error',
        messageKey,
      })
    },
    setSnackbarData({ type = 'info', messageKey }) {
      Object.assign(this.snackbar, { type, messageKey })
      this.isSnackbarVisible = true
    },
    setEmailDialogVisibility(isVisible) {
      this.isEmailDialogVisible = isVisible
    },
    async attachFormSubmission(formSubmissionId) {
      await this.attachFormSubmissionId({
        jobId: this.jobUuid,
        formId: this.selectedFormId,
        formSubmissionId,
      })
      if (!this.ticketsError) {
        this.setJobProperties(this.getJobById(this.jobId))
        this.setSnackbarData({ messageKey: 'attachedForm' })
      } else {
        this.setSnackbarData({
          type: 'error',
          messageKey: 'attachedFormError',
        })
      }
    },
    updatedAttachedFormSubmission() {
      this.closeFormSubmission()
      this.setSnackbarData({
        messageKey: 'updatedAttachedFormSubmission',
      })
    },
    async attachFormRequirement(formSubmissionId) {
      await this.attachFormRequirementSubmission({
        jobId: this.jobUuid,
        formRequirementId: this.selectedFormRequirement.id,
        formSubmissionId,
      })
      if (!this.ticketsError) {
        this.setJobProperties(this.getJobById(this.jobId))
        this.setSnackbarData({ messageKey: 'attachFormRequirement' })
      } else {
        this.setSnackbarData({
          type: 'error',
          messageKey: 'attachFormRequirementError',
        })
      }
    },
    updatedFormRequirementSubmission() {
      this.closeFormRequirementBottomSheet()
      this.setSnackbarData({
        messageKey: 'updatedFormRequirementSubmission',
      })
    },
    async setJobHistory() {
      await this.queryClient.invalidateQueries({
        queryKey: ['history', 'byJobNumber', this.jobId],
      })
    },
    async removeJobFromGroup() {
      await this.removeJobFromAssignmentGroup(this.jobId)
      if (!this.ticketsError) {
        this.setJobProperties(this.getJobById(this.jobId))
        this.setSnackbarData({ messageKey: 'removedJobFromGroup' })
      } else {
        this.setSnackbarData({
          type: 'error',
          messageKey: 'removeJobFromGroupError',
        })
      }
    },
    clearSignature() {
      this.signature = null
      this.setSnackbarData({ messageKey: 'signatureCleared' })
    },
    addSignature(signature) {
      this.signature = signature
      this.setSnackbarData({ messageKey: 'signatureAdded' })
    },
    updatePoolExecutionPlan(value) {
      this.poolExecutionPlan = value
    },
    updateAssignmentFormRequirements(requirements) {
      this.assignmentFormRequirements = requirements
    },
    clearAssignedPool() {
      this.poolId = null
    },
    async handlePoolClosed() {
      if (!this.draftChangesDetected) {
        await this.fetchJob(this.jobId)
        this.setJobProperties(this.getJobById(this.jobId))
      }
    },
  },
}
</script>

<style scoped>
.description {
  margin-top: calc(-1 * var(--space-base));
  margin-bottom: var(--space-small);
}

.back-button {
  --text-color: var(--color-grey);
  color: var(--color-grey);
  margin-right: var(--space-base);
  padding: var(--space-smallest);
}

.back-button:hover,
.back-button:focus {
  background-color: #f4f4f4;
}
</style>
